// src/pages/Members.jsx — Centre administratif des membres
// Architecture : Interface Admin → Worker Admin → Worker Paiement
// Aucun appel direct au Worker Paiement depuis le navigateur.
import React, { useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  Users,
  Search,
  Filter,
  Eye,
  X,
  Ban,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  AreaChart,
  EmptyState,
} from '../components/ui/UIComponents';
import {
  getUtilisateurs,
  getMembreFiche,
  suspendreMembre,
  reactiverMembre,
} from '../services/adminMembres';
import { useI18n } from '../i18n/useI18n';

const CHART = ['#10b981', '#f43f5e', '#6366f1', '#f59e0b', '#0ea5e9', '#8b5cf6'];

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'list', label: 'Tous les membres', icon: Users },
];

function dt(iso) {
  if (iso === undefined || iso === null || iso === '') return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString('fr-FR');
  } catch {
    return String(iso);
  }
}

function display(value) {
  if (value === undefined || value === null || value === '') return '—';
  return value;
}

function statusKey(s) {
  const v = (s || '').toString().toLowerCase();
  if (v === 'actif' || v === 'active') return 'ACTIF';
  if (v === 'suspendu' || v === 'suspended') return 'SUSPENDU';
  return 'INACTIF';
}

/** Extrait une liste depuis la réponse relais GET /admin/utilisateurs */
function extractMembersList(response) {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== 'object') return [];
  for (const key of ['utilisateurs', 'members', 'membres', 'data', 'users']) {
    if (Array.isArray(response[key])) return response[key];
  }
  return [];
}

/**
 * Normalise un élément de liste pour l'UI sans inventer de valeurs.
 * N'utilise que les champs présents ; sinon "—".
 * Ne lit jamais motDePasseHash.
 */
function normalizeMemberRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const identifiantDsi =
    raw.identifiantDsi || raw.identifiant_dsi || raw.id || null;
  return {
    raw,
    identifiantDsi,
    nom: raw.nom ?? null,
    prenom: raw.prenom ?? null,
    email: raw.email ?? null,
    whatsapp: raw.whatsapp ?? raw.telephone ?? null,
    pays: raw.pays ?? null,
    statut: raw.statut ?? raw.status ?? null,
    dateInscription:
      raw.dateInscription ||
      raw.date_inscription ||
      raw.created_at ||
      raw.date_creation ||
      null,
    dateActivation:
      raw.dateActivation || raw.date_activation || raw.activated_at || null,
    codeParrainagePersonnel:
      raw.codeParrainagePersonnel || raw.code_parrainage_personnel || null,
  };
}

/** Accepte la fiche au niveau racine ou encapsulée (fiche / data) */
function normalizeFichePayload(response) {
  if (!response || typeof response !== 'object') return null;
  if (response.profil) return response;
  if (response.fiche && typeof response.fiche === 'object') return response.fiche;
  if (response.data && typeof response.data === 'object' && response.data.profil) {
    return response.data;
  }
  return response;
}

function VBars({ items = [] }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="flex items-end justify-between gap-2 h-36 px-1">
      {items.map((it) => (
        <div key={it.label} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <span className="text-[10px] font-bold text-slate-600">{it.value}</span>
          <div
            className="w-full max-w-[56px] rounded-t-lg transition-all duration-700"
            style={{
              height: `${Math.max(8, Math.round((it.value / max) * 100))}%`,
              background: it.color || '#4f46e5',
            }}
            title={`${it.label}: ${it.value}`}
          />
          <span className="text-[10px] text-slate-500 truncate w-full text-center">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

function TruncationHint({ meta, sectionKey, label }) {
  const tronque = meta?.tronque;
  if (!tronque || typeof tronque !== 'object') return null;
  const flag = tronque[sectionKey];
  if (flag !== true) return null;
  return (
    <p className="text-[11px] text-amber-700 font-medium mt-1">
      {label || 'Liste tronquée'} — affichage partiel selon les limites du contrat.
    </p>
  );
}

export default function Members() {
  const { t } = useI18n();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [fiche, setFiche] = useState(null);
  const [ficheLoading, setFicheLoading] = useState(false);
  const [ficheError, setFicheError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type, id, motif }
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'ok'|'err', message }

  const showToast = (type, message) => {
    setToast({ type, message });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUtilisateurs();
      const list = extractMembersList(res)
        .map(normalizeMemberRow)
        .filter((m) => m && m.identifiantDsi);
      setMembers(list);
    } catch (err) {
      setMembers([]);
      setError(err.message || 'Impossible de charger les membres.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Agrégats uniquement depuis la liste réelle reçue
  const listStats = useMemo(() => {
    const total = members.length;
    const actifs = members.filter((m) => {
      const s = (m.statut || '').toString().toLowerCase();
      return s === 'actif' || s === 'active';
    }).length;
    const suspendus = members.filter((m) => {
      const s = (m.statut || '').toString().toLowerCase();
      return s === 'suspendu' || s === 'suspended';
    }).length;
    return { total, actifs, suspendus };
  }, [members]);

  const statusBars = useMemo(
    () => [
      { label: 'Actifs', value: listStats.actifs, color: '#10b981' },
      { label: 'Suspendus', value: listStats.suspendus, color: '#f43f5e' },
    ],
    [listStats]
  );

  const statusDonut = useMemo(
    () =>
      statusBars
        .filter((s) => s.value > 0)
        .map((s) => ({ label: s.label, value: s.value, color: s.color })),
    [statusBars]
  );

  const countryBars = useMemo(() => {
    const map = {};
    members.forEach((m) => {
      if (!m.pays) return;
      map[m.pays] = (map[m.pays] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value, color: 'bg-indigo-500' }));
  }, [members]);

  /** Séries dérivées uniquement des dates réellement présentes */
  const inscriptionTimeline = useMemo(() => {
    const buckets = {};
    members.forEach((m) => {
      if (!m.dateInscription) return;
      const d = new Date(m.dateInscription);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 7);
      if (!buckets[key]) buckets[key] = { inscriptions: 0, activations: 0 };
      buckets[key].inscriptions += 1;
      if (m.dateActivation) buckets[key].activations += 1;
    });
    const keys = Object.keys(buckets).sort();
    if (keys.length < 2) return null;
    return {
      labels: keys,
      inscriptions: keys.map((k) => buckets[k].inscriptions),
      activations: keys.map((k) => buckets[k].activations),
      compare: keys.map((k) => ({
        label: k,
        inscriptions: buckets[k].inscriptions,
        activations: buckets[k].activations,
      })),
    };
  }, [members]);

  const countries = useMemo(
    () => [...new Set(members.map((m) => m.pays).filter(Boolean))].sort(),
    [members]
  );

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (statusFilter) {
        const s = (m.statut || '').toString().toLowerCase();
        if (s !== statusFilter.toLowerCase()) return false;
      }
      if (countryFilter && m.pays !== countryFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const blob = `${m.identifiantDsi || ''} ${m.nom || ''} ${m.prenom || ''} ${m.email || ''} ${m.whatsapp || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [members, search, statusFilter, countryFilter]);

  const openFiche = async (identifiantDsi) => {
    setFicheLoading(true);
    setFicheError(null);
    setFiche(null);
    try {
      const res = await getMembreFiche(identifiantDsi);
      const normalized = normalizeFichePayload(res);
      if (!normalized || !normalized.profil) {
        setFicheError('Réponse de fiche inattendue ou incomplète.');
        setTab('list');
        return;
      }
      setFiche(normalized);
      setTab('fiche');
    } catch (err) {
      if (err.status === 404) {
        setFicheError(`Membre introuvable (${identifiantDsi}).`);
      } else {
        setFicheError(err.message || 'Impossible de charger la fiche membre.');
      }
      setTab('list');
    } finally {
      setFicheLoading(false);
    }
  };

  const runAction = async () => {
    if (!confirmAction || actionLoading) return;
    const { type, id, motif } = confirmAction;
    setActionLoading(true);
    try {
      let result;
      if (type === 'suspend') {
        result = await suspendreMembre(id, motif);
      } else {
        result = await reactiverMembre(id, motif);
      }

      const newStatut =
        result?.statut ||
        (type === 'suspend' ? 'suspendu' : 'actif');

      setMembers((prev) =>
        prev.map((m) =>
          m.identifiantDsi === id ? { ...m, statut: newStatut } : m
        )
      );

      if (fiche?.profil?.identifiantDsi === id) {
        setFiche((f) => ({
          ...f,
          profil: { ...f.profil, statut: newStatut },
          abonnement: f.abonnement
            ? { ...f.abonnement, statut: newStatut }
            : f.abonnement,
        }));
      }

      showToast(
        'ok',
        type === 'suspend'
          ? `Membre ${id} suspendu${result?.ancienStatut ? ` (était : ${result.ancienStatut})` : ''}.`
          : `Membre ${id} réactivé${result?.ancienStatut ? ` (était : ${result.ancienStatut})` : ''}.`
      );
      setConfirmAction(null);
    } catch (err) {
      showToast('err', err.message || 'Action impossible.');
    } finally {
      setActionLoading(false);
    }
  };

  const payStatusBars = useMemo(() => {
    if (!fiche?.paiements?.length) return [];
    const map = {};
    fiche.paiements.forEach((p) => {
      const s = p?.statut || 'inconnu';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], i) => ({
      label,
      value,
      color: CHART[i % CHART.length],
    }));
  }, [fiche]);

  const wStatusBars = useMemo(() => {
    if (!fiche?.retraits?.length) return [];
    const map = {};
    fiche.retraits.forEach((r) => {
      const s = r?.statut || 'inconnu';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], i) => ({
      label,
      value,
      color: CHART[i % CHART.length],
    }));
  }, [fiche]);

  const sectionsEnErreur = Array.isArray(fiche?.meta?.sectionsEnErreur)
    ? fiche.meta.sectionsEnErreur
    : [];

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t('pages.members.title')}
        subtitle="Centre administratif des membres — consultation, filtres et fiche."
        actions={
          <button type="button" onClick={load} className="dsi-btn-secondary text-xs" disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('common.refresh')}
          </button>
        }
      />

      {toast && (
        <div
          className={`p-3 rounded-2xl border text-xs font-bold flex justify-between ${
            toast.type === 'err'
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}
        >
          <span>{toast.message}</span>
          <button type="button" className="underline" onClick={() => setToast(null)}>
            {t('common.close')}
          </button>
        </div>
      )}

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-center justify-between text-rose-700 text-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
          <button type="button" onClick={load} className="dsi-btn-secondary text-xs">
            {t('common.retry')}
          </button>
        </div>
      )}

      {ficheError && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-between text-amber-800 text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>{ficheError}</span>
          </div>
          <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setFicheError(null)}>
            {t('common.close')}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.label}
            </button>
          );
        })}
        {fiche && (
          <button
            type="button"
            onClick={() => setTab('fiche')}
            className={`px-3 py-2 rounded-xl text-xs font-semibold ${
              tab === 'fiche' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            Fiche {fiche.profil?.identifiantDsi}
          </button>
        )}
      </div>

      {loading ? (
        <div className="dsi-card p-12 text-center text-xs text-slate-500">{t('common.loading')}</div>
      ) : (
        <>
          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ['Total membres', listStats.total],
                  ['Actifs', listStats.actifs],
                  ['Suspendus', listStats.suspendus],
                ].map(([label, value]) => (
                  <div key={label} className="dsi-card p-4">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {typeof value === 'number' ? value.toLocaleString('fr-FR') : display(value)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">Calculé sur la liste reçue</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-1">Histogramme des statuts</h3>
                  <p className="text-xs text-slate-500 mb-3">
                    Calculé sur les {listStats.total} membres reçus
                  </p>
                  {listStats.total > 0 ? (
                    <VBars items={statusBars} />
                  ) : (
                    <EmptyState title="Aucun membre" message="La liste est vide." />
                  )}
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-3">Répartition des statuts</h3>
                  {statusDonut.length ? (
                    <DonutChart segments={statusDonut} centerValue={listStats.total} centerLabel="Liste" />
                  ) : (
                    <EmptyState title={t('common.empty')} />
                  )}
                </div>
              </div>

              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold mb-1">Évolution inscriptions / activations</h3>
                <p className="text-xs text-slate-500 mb-3">
                  Dérivée uniquement des dates présentes dans les objets reçus
                </p>
                {inscriptionTimeline ? (
                  <>
                    <AreaChart series={inscriptionTimeline.inscriptions} />
                    <div className="mt-2 flex justify-between text-[10px] text-slate-400">
                      {inscriptionTimeline.labels.map((l) => (
                        <span key={l}>{l}</span>
                      ))}
                    </div>
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Comparaison mensuelle</p>
                      <HorizontalBars
                        rows={inscriptionTimeline.compare.flatMap((c) => [
                          { label: `${c.label} insc.`, value: c.inscriptions, color: 'bg-indigo-500' },
                          { label: `${c.label} act.`, value: c.activations, color: 'bg-emerald-500' },
                        ])}
                      />
                    </div>
                  </>
                ) : (
                  <EmptyState
                    title="Données temporelles indisponibles"
                    message="Pas assez de dates distinctes pour construire une série fiable."
                  />
                )}
              </div>

              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold mb-3">Répartition géographique (champ pays)</h3>
                {countryBars.length ? (
                  <HorizontalBars rows={countryBars} />
                ) : (
                  <EmptyState title="Aucun pays" message="Aucun champ pays exploitable dans la liste reçue." />
                )}
              </div>
            </div>
          )}

          {/* LIST */}
          {tab === 'list' && (
            <div className="space-y-4">
              <div className="dsi-card p-4 flex flex-col lg:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    className="dsi-input pl-9"
                    placeholder="Identifiant DSI, nom, prénom, email, WhatsApp…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Filter size={14} className="text-slate-400" />
                  <select
                    className="dsi-input w-auto"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Tous statuts</option>
                    <option value="actif">Actif</option>
                    <option value="suspendu">Suspendu</option>
                  </select>
                  <select
                    className="dsi-input w-auto"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                  >
                    <option value="">Tous pays</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="dsi-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3">Membre</th>
                        <th className="px-4 py-3">Identifiant</th>
                        <th className="px-4 py-3">Contact</th>
                        <th className="px-4 py-3">Pays</th>
                        <th className="px-4 py-3">Statut</th>
                        <th className="px-4 py-3">Inscription</th>
                        <th className="px-4 py-3">Activation</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filtered.map((m) => (
                        <tr key={m.identifiantDsi} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">
                            {display(m.prenom)} {display(m.nom)}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{display(m.identifiantDsi)}</td>
                          <td className="px-4 py-3 text-xs">
                            <div>{display(m.email)}</div>
                            <div className="text-slate-400">{display(m.whatsapp)}</div>
                          </td>
                          <td className="px-4 py-3">{display(m.pays)}</td>
                          <td className="px-4 py-3">
                            <StatusBadge statusKey={statusKey(m.statut)} text={display(m.statut)} />
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{dt(m.dateInscription)}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{dt(m.dateActivation)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              className="dsi-btn-ghost text-xs"
                              onClick={() => openFiche(m.identifiantDsi)}
                              disabled={ficheLoading}
                            >
                              <Eye size={14} /> Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!filtered.length && (
                  <EmptyState
                    title="Aucun membre"
                    message={
                      members.length
                        ? 'Ajustez recherche ou filtres.'
                        : 'Aucune donnée reçue de GET /admin/utilisateurs.'
                    }
                  />
                )}
                <p className="px-4 py-2 text-[10px] text-slate-500 bg-slate-50 border-t border-slate-100">
                  Source : GET /admin/utilisateurs · {filtered.length} affiché
                  {filtered.length !== members.length ? ` / ${members.length} total` : ''}
                </p>
              </div>
            </div>
          )}

          {/* FICHE */}
          {tab === 'fiche' && fiche && (
            <div className="space-y-4">
              {sectionsEnErreur.length > 0 && (
                <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                  <p className="font-bold flex items-center gap-2">
                    <AlertTriangle size={14} /> Données partielles
                  </p>
                  <p className="mt-1">
                    Sections en erreur côté Worker : {sectionsEnErreur.join(', ')}
                  </p>
                </div>
              )}

              <div className="dsi-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    {display(fiche.profil?.prenom)} {display(fiche.profil?.nom)}
                  </h2>
                  <p className="text-sm text-slate-500 font-mono">
                    {display(fiche.profil?.identifiantDsi)}
                  </p>
                  <div className="mt-2">
                    <StatusBadge
                      statusKey={statusKey(fiche.profil?.statut)}
                      text={display(fiche.profil?.statut)}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {statusKey(fiche.profil?.statut) === 'ACTIF' && (
                    <button
                      type="button"
                      className="dsi-btn-secondary text-xs text-rose-700 border-rose-200"
                      onClick={() =>
                        setConfirmAction({
                          type: 'suspend',
                          id: fiche.profil.identifiantDsi,
                          motif: '',
                        })
                      }
                    >
                      <Ban size={14} /> Suspendre
                    </button>
                  )}
                  {statusKey(fiche.profil?.statut) === 'SUSPENDU' && (
                    <button
                      type="button"
                      className="dsi-btn-secondary text-xs text-emerald-700 border-emerald-200"
                      onClick={() =>
                        setConfirmAction({
                          type: 'reactivate',
                          id: fiche.profil.identifiantDsi,
                          motif: '',
                        })
                      }
                    >
                      <UserCheck size={14} /> Réactiver
                    </button>
                  )}
                  <button
                    type="button"
                    className="dsi-btn-ghost text-xs"
                    onClick={() => {
                      setFiche(null);
                      setTab('list');
                    }}
                  >
                    <X size={14} /> Fermer
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {[
                  ['Email', fiche.profil?.email],
                  ['WhatsApp', fiche.profil?.whatsapp],
                  ['Pays', fiche.profil?.pays],
                  ['Code parrainage', fiche.profil?.codeParrainagePersonnel],
                  [
                    'Abonnement',
                    fiche.abonnement
                      ? `${display(fiche.abonnement.plan)} · ${display(fiche.abonnement.statut)}`
                      : null,
                  ],
                  [
                    'Niveau',
                    fiche.niveau
                      ? `${display(fiche.niveau.code)} · ${display(fiche.niveau.libelle)}`
                      : null,
                  ],
                  ['Inscription', dt(fiche.profil?.dateInscription)],
                  ['Activation', dt(fiche.profil?.dateActivation)],
                ].map(([k, v]) => (
                  <div key={k} className="dsi-card p-4">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">{k}</p>
                    <p className="text-sm font-bold text-slate-900 mt-1 break-all">{display(v)}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="dsi-card p-5 md:col-span-1">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold">Solde DSI</p>
                  <p className="text-3xl font-bold text-indigo-700 mt-2">
                    {fiche.solde?.dsi != null
                      ? Number(fiche.solde.dsi).toLocaleString('fr-FR')
                      : '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {fiche.solde?.fcfaEquivalent != null
                      ? `≈ ${Number(fiche.solde.fcfaEquivalent).toLocaleString('fr-FR')} XOF`
                      : '—'}
                  </p>
                </div>
                <div className="dsi-card p-5">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold mb-2">Limites retrait</p>
                  {fiche.limitesRetrait ? (
                    <>
                      <p className="text-sm">
                        Min {display(fiche.limitesRetrait.minDsi)} · Max{' '}
                        {display(fiche.limitesRetrait.maxDsi)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Restant aujourd&apos;hui : {display(fiche.limitesRetrait.restanteAujourdhui)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-400">—</p>
                  )}
                </div>
                <div className="dsi-card p-5">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold mb-2">Parrainage</p>
                  {fiche.parrainage ? (
                    <>
                      <p className="text-sm font-bold">
                        {display(fiche.parrainage.filleuls)} filleuls
                      </p>
                      <p className="text-xs text-slate-500">
                        Bonus {display(fiche.parrainage.bonusDsi)} DSI · niveau{' '}
                        {display(fiche.parrainage.niveau)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-400">—</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Paiements</h3>
                  <TruncationHint meta={fiche.meta} sectionKey="paiements" label="Paiements tronqués" />
                  {payStatusBars.length ? (
                    <VBars items={payStatusBars} />
                  ) : (
                    <EmptyState title="Aucun paiement" />
                  )}
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="py-1">ID</th>
                          <th>Statut</th>
                          <th>Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(fiche.paiements || []).map((p, idx) => (
                          <tr
                            key={p.idTransaction || p.id || `pay-${idx}`}
                            className="border-t border-slate-100"
                          >
                            <td className="py-1 font-mono">{display(p.idTransaction || p.id)}</td>
                            <td>{display(p.statut)}</td>
                            <td className="font-semibold">
                              {p.montant != null
                                ? `${Number(p.montant).toLocaleString('fr-FR')} ${display(p.devise)}`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Retraits</h3>
                  <TruncationHint meta={fiche.meta} sectionKey="retraits" label="Retraits tronqués" />
                  {wStatusBars.length ? (
                    <VBars items={wStatusBars} />
                  ) : (
                    <EmptyState title="Aucun retrait" />
                  )}
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="py-1">ID</th>
                          <th>Statut</th>
                          <th>FCFA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(fiche.retraits || []).map((r, idx) => (
                          <tr
                            key={r.idRetrait || r.id || `ret-${idx}`}
                            className="border-t border-slate-100"
                          >
                            <td className="py-1 font-mono">{display(r.idRetrait || r.id)}</td>
                            <td>{display(r.statut)}</td>
                            <td className="font-semibold">
                              {r.montantFcfa != null
                                ? Number(r.montantFcfa).toLocaleString('fr-FR')
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Sections secondaires prévues par le contrat */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Comptabilité</h3>
                  <TruncationHint meta={fiche.meta} sectionKey="comptabilite" />
                  {(fiche.comptabilite || []).length ? (
                    <ul className="text-xs space-y-1">
                      {fiche.comptabilite.map((c, i) => (
                        <li key={c.id || i} className="flex justify-between gap-2 border-b border-slate-50 py-1">
                          <span>{display(c.libelle || c.label)}</span>
                          <span className="font-semibold">
                            {c.montant != null ? Number(c.montant).toLocaleString('fr-FR') : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-400">—</p>
                  )}
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Codes utilisés</h3>
                  <TruncationHint meta={fiche.meta} sectionKey="codesUtilises" />
                  {(fiche.codesUtilises || []).length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {fiche.codesUtilises.map((code, i) => (
                        <span
                          key={typeof code === 'string' ? code : i}
                          className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-mono"
                        >
                          {typeof code === 'string' ? code : display(code?.code || code?.id)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">—</p>
                  )}
                </div>
                <div className="dsi-card p-5 md:col-span-2">
                  <h3 className="text-sm font-semibold mb-2">Prolongations</h3>
                  <TruncationHint meta={fiche.meta} sectionKey="prolongations" />
                  {(fiche.prolongations || []).length ? (
                    <ul className="text-xs space-y-1">
                      {fiche.prolongations.map((p, i) => (
                        <li key={p.id || i} className="border-b border-slate-50 py-1">
                          {display(p.jours)} jours · {dt(p.date)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-400">—</p>
                  )}
                </div>
              </div>

              <div className="dsi-card p-4 text-xs text-slate-500">
                Aucun secret ni motDePasseHash exposé. Actions via Worker Admin uniquement
                (POST /admin/membres/suspendre · /admin/membres/reactiver).
              </div>
            </div>
          )}
        </>
      )}

      {ficheLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/20">
          <div className="dsi-card px-6 py-4 text-xs font-semibold text-slate-600">
            Chargement de la fiche…
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !actionLoading && setConfirmAction(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={18} />
              <h3 className="font-bold">
                {confirmAction.type === 'suspend' ? 'Suspendre ce membre ?' : 'Réactiver ce membre ?'}
              </h3>
            </div>
            <p className="text-xs text-slate-600">
              {confirmAction.type === 'suspend'
                ? 'Cette action modifiera le statut administratif du membre via Worker Admin.'
                : 'Le membre retrouvera un statut actif via Worker Admin.'}
            </p>
            <textarea
              className="dsi-input min-h-[80px]"
              placeholder="Motif (optionnel)"
              value={confirmAction.motif || ''}
              onChange={(e) => setConfirmAction({ ...confirmAction, motif: e.target.value })}
              disabled={actionLoading}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="dsi-btn-secondary text-xs"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="dsi-btn-primary text-xs"
                onClick={runAction}
                disabled={actionLoading}
              >
                {actionLoading ? '…' : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
